// MessageInput.js
import React, { useCallback, useState, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Textarea,
    Button,
    FormControl,
    Stack,
    Flex,
    Icon,
    IconButton,
    Collapse,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useBreakpointValue,
    Tooltip,
} from '@chakra-ui/react';
import {
    MdOutlineArrowUpward,
    MdFullscreen,
    MdTune,
    MdExpandLess,
    MdExpandMore,
} from 'react-icons/md';
import ModelSelector from './ModelSelector';
import SystemPromptSelector from './SystemPromptSelector';
import QuickTemplatesMenu from './QuickTemplatesMenu';
import { useModelOptions, useSystemPromptOptions } from './hooks/useOptions';
import DocumentQuickAccess from './DocumentQuickAccess';

// ─── Keyboard hints ────────────────────────────────────────────────────────────

const KeyboardHints = ({ isModal = false }) => {
    const isMobile = useBreakpointValue({ base: true, md: false });
    if (isMobile) return null;

    const kbdStyle = {
        bg: 'gray.100',
        border: '1px solid',
        borderColor: 'gray.300',
        borderRadius: 'sm',
        px: 2,
        py: '2px',
        fontSize: 'xs',
        fontFamily: 'mono',
        fontWeight: 'semibold',
        color: 'gray.700',
    };

    return (
        <HStack spacing={4} fontSize="xs" color="text.muted">
            {!isModal ? (
                <>
                    <HStack spacing={1}>
                        <Box as="kbd" {...kbdStyle}>Enter</Box>
                        <Text>Send</Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Box as="kbd" {...kbdStyle}>Shift+Enter</Box>
                        <Text>New line</Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Box as="kbd" {...kbdStyle}>Ctrl+E</Box>
                        <Text>Expand</Text>
                    </HStack>
                </>
            ) : (
                <>
                    <HStack spacing={1}>
                        <Box as="kbd" {...kbdStyle}>Ctrl+Enter</Box>
                        <Text>Send</Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Box as="kbd" {...kbdStyle}>Enter</Box>
                        <Text>New line</Text>
                    </HStack>
                </>
            )}
        </HStack>
    );
};

// ─── Textarea DOM helpers ──────────────────────────────────────────────────────

const getRefValue = (ref) => ref.current?.value ?? '';

const setRefValue = (ref, value, caretPos = null) => {
    if (!ref.current) return;
    ref.current.value = value;
    if (caretPos !== null) {
        ref.current.selectionStart = caretPos;
        ref.current.selectionEnd = caretPos;
    }
};

/**
 * Auto-resize a textarea to fit its content, between min and max heights.
 * Works with uncontrolled textareas.
 */
const autoResize = (textarea, minHeight, maxHeight) => {
    if (!textarea) return;
    // Reset to min so shrinking works when text is deleted
    textarea.style.height = `${minHeight}px`;
    // Grow to fit content, capped at max
    const scrollH = textarea.scrollHeight;
    textarea.style.height = `${Math.min(Math.max(scrollH, minHeight), maxHeight)}px`;
};

// ─── Main component ────────────────────────────────────────────────────────────

const MessageInput = React.memo(({
    handleSubmit,
    model,
    setModel,
    systemPrompt,
    setSystemPrompt,
    isLoading,
}) => {
    const [showSettings, setShowSettings] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const desktopTextareaRef = useRef(null);
    const mobileTextareaRef = useRef(null);
    const modalTextareaRef = useRef(null);

    const selectionSnapshotRef = useRef(null);

    const { options: modelOptions } = useModelOptions();
    const { options: systemPromptOptions } = useSystemPromptOptions();

    const isMobile = useBreakpointValue({ base: true, md: false });
    const modalSize = useBreakpointValue({ base: 'full', md: 'xl', lg: '4xl', xl: '6xl' });

    // Mobile: single-line height constants (in px)
    // 1 line ≈ 42px, 4 lines ≈ 120px
    const MOBILE_MIN_H = 42;
    const MOBILE_MAX_H = 120;

    const placeholderText = isMobile
        ? 'Type a message...'
        : 'Type your message... (Enter to send, Shift+Enter for new line, Ctrl+E to expand)';

    const modalPlaceholderText = isMobile
        ? 'Type your message here...'
        : 'Type your message here... (Ctrl+Enter to send, Enter for new line)';

    // ── Label helpers ──────────────────────────────────────────────────────────

    const getModelLabel = (modelValue) => {
        const option = modelOptions.find(opt => opt.value === modelValue);
        return option ? option.label : modelValue?.split('-')[0] ?? modelValue;
    };

    const getSystemPromptLabel = (promptValue) => {
        const option = systemPromptOptions.find(opt => opt.value === promptValue);
        return option ? option.label : promptValue;
    };

    // ── Active textarea resolution ─────────────────────────────────────────────

    const getActiveTextarea = useCallback(() => {
        const ae = document.activeElement;
        if (ae === desktopTextareaRef.current) return desktopTextareaRef.current;
        if (ae === mobileTextareaRef.current) return mobileTextareaRef.current;
        const isVisible = (el) => !!el && el.offsetParent !== null;
        if (isVisible(desktopTextareaRef.current)) return desktopTextareaRef.current;
        if (isVisible(mobileTextareaRef.current)) return mobileTextareaRef.current;
        return desktopTextareaRef.current ?? mobileTextareaRef.current;
    }, []);

    // ── Selection snapshot ─────────────────────────────────────────────────────

    const captureSelectionNow = useCallback(() => {
        const ta = getActiveTextarea();
        if (!ta) { selectionSnapshotRef.current = null; return; }
        const start = ta.selectionStart ?? ta.value.length;
        const end = ta.selectionEnd ?? ta.value.length;
        selectionSnapshotRef.current = { start, end, text: ta.value.slice(start, end) };
    }, [getActiveTextarea]);

    const getCapturedSelection = useCallback(() => selectionSnapshotRef.current, []);

    // ── getValue / setValue ─────────────────────────────────────────────────────

    const getMainInputValue = useCallback(
        () => getActiveTextarea()?.value ?? '',
        [getActiveTextarea]
    );

    const setMainInputValue = useCallback((newValue, caretPos = null) => {
        const ta = getActiveTextarea();
        if (!ta) return;
        ta.value = newValue;
        if (caretPos !== null) { ta.selectionStart = caretPos; ta.selectionEnd = caretPos; }
        // Re-fit after programmatic value change (template insert, doc insert)
        if (ta === mobileTextareaRef.current) autoResize(ta, MOBILE_MIN_H, MOBILE_MAX_H);
    }, [getActiveTextarea, MOBILE_MIN_H, MOBILE_MAX_H]);

    // ── Clear ──────────────────────────────────────────────────────────────────

    const clearMainInput = useCallback(() => {
        if (desktopTextareaRef.current) desktopTextareaRef.current.value = '';
        if (mobileTextareaRef.current) {
            mobileTextareaRef.current.value = '';
            autoResize(mobileTextareaRef.current, MOBILE_MIN_H, MOBILE_MAX_H);
        }
    }, [MOBILE_MIN_H, MOBILE_MAX_H]);

    // ── Submit ─────────────────────────────────────────────────────────────────

    const handleFormSubmit = useCallback((e) => {
        e.preventDefault();
        const value = getActiveTextarea()?.value ?? '';
        if (!value.trim()) return;
        handleSubmit(e, value.trim());
        clearMainInput();
    }, [handleSubmit, clearMainInput, getActiveTextarea]);

    // ── Mobile auto-resize on input ────────────────────────────────────────────

    const handleMobileInput = useCallback(() => {
        autoResize(mobileTextareaRef.current, MOBILE_MIN_H, MOBILE_MAX_H);
    }, [MOBILE_MIN_H, MOBILE_MAX_H]);

    // ── Modal ──────────────────────────────────────────────────────────────────

    const openModal = useCallback(() => {
        const currentValue = getActiveTextarea()?.value ?? '';
        setIsModalOpen(true);
        requestAnimationFrame(() => { setRefValue(modalTextareaRef, currentValue); });
    }, [getActiveTextarea]);

    const closeModal = useCallback(() => {
        const modalValue = getRefValue(modalTextareaRef);
        setMainInputValue(modalValue);
        setIsModalOpen(false);
    }, [setMainInputValue]);

    const sendFromModal = useCallback((e) => {
        const modalValue = getRefValue(modalTextareaRef);
        if (!modalValue.trim()) return;
        handleSubmit(e, modalValue.trim());
        setRefValue(modalTextareaRef, '');
        clearMainInput();
        setIsModalOpen(false);
    }, [handleSubmit, clearMainInput]);

    // ── Keyboard ───────────────────────────────────────────────────────────────

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleFormSubmit(e);
        }
        if (!isMobile && (e.ctrlKey || e.metaKey) && e.key === 'e') {
            e.preventDefault();
            openModal();
        }
    }, [isMobile, handleFormSubmit, openModal]);

    const handleModalKeyDown = useCallback((e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            sendFromModal(e);
        }
    }, [sendFromModal]);

    // ── Document insert ────────────────────────────────────────────────────────

    const handleDocumentInsert = useCallback((text) => {
        const ta = getActiveTextarea();
        if (!ta) return;
        ta.value = ta.value + text;
        ta.focus();
        if (ta === mobileTextareaRef.current) autoResize(ta, MOBILE_MIN_H, MOBILE_MAX_H);
    }, [getActiveTextarea, MOBILE_MIN_H, MOBILE_MAX_H]);

    const handleModalDocumentInsert = useCallback((text) => {
        if (!modalTextareaRef.current) return;
        modalTextareaRef.current.value = modalTextareaRef.current.value + text;
        modalTextareaRef.current.focus();
    }, []);

    const toggleSettings = useCallback(() => setShowSettings(prev => !prev), []);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            <Box
                bg="bg.primary"
                borderTop="1px"
                borderColor="border.primary"
                p={{ base: 2, md: 4 }}
                // On mobile, leave room for the bottom tab bar
                pb={{ base: 'calc(8px + 48px + env(safe-area-inset-bottom))', md: 4 }}
            >
                <Box maxW="4xl" mx="auto">
                    <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
                        <VStack spacing={{ base: 2, md: 4 }}>

                            {/* ── Settings: mobile/tablet ──────────────────── */}
                            <Flex
                                w="full"
                                justify="space-between"
                                align="center"
                                display={{ base: 'flex', lg: 'none' }}
                            >
                                <HStack
                                    spacing={1}
                                    color="text.secondary"
                                    fontSize="xs"
                                    cursor="pointer"
                                    onClick={toggleSettings}
                                    _hover={{ color: 'text.primary' }}
                                >
                                    <MdTune size="14px" />
                                    <Text fontWeight="500" isTruncated maxW="250px">
                                        {getModelLabel(model)} · {getSystemPromptLabel(systemPrompt)}
                                    </Text>
                                    <Icon
                                        as={showSettings ? MdExpandMore : MdExpandLess}
                                        boxSize="16px"
                                    />
                                </HStack>
                            </Flex>

                            <Box w="full">
                                <Collapse in={showSettings} animateOpacity>
                                    <Stack
                                        direction="column"
                                        spacing={2}
                                        w="full"
                                        display={{ base: 'flex', lg: 'none' }}
                                        pb={2}
                                        borderBottom="1px"
                                        borderColor="border.primary"
                                    >
                                        <FormControl>
                                            <SystemPromptSelector
                                                systemPrompt={systemPrompt}
                                                setSystemPrompt={setSystemPrompt}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <ModelSelector model={model} setModel={setModel} />
                                        </FormControl>
                                    </Stack>
                                </Collapse>

                                {/* ── Settings: desktop ──────────────────────── */}
                                <Stack
                                    direction="row"
                                    spacing={4}
                                    w="full"
                                    align="flex-end"
                                    display={{ base: 'none', lg: 'flex' }}
                                >
                                    <FormControl flex="1" minW="170px">
                                        <SystemPromptSelector
                                            systemPrompt={systemPrompt}
                                            setSystemPrompt={setSystemPrompt}
                                        />
                                    </FormControl>
                                    <FormControl flex="1" minW="170px">
                                        <ModelSelector model={model} setModel={setModel} />
                                    </FormControl>
                                </Stack>
                            </Box>

                            {/* ── Input: desktop ───────────────────────────── */}
                            <Box w="full">
                                <VStack spacing={3} display={{ base: 'none', lg: 'flex' }}>
                                    <Box position="relative" w="full">
                                        <Textarea
                                            ref={desktopTextareaRef}
                                            defaultValue=""
                                            variant="messageInput"
                                            placeholder={placeholderText}
                                            resize="none"
                                            minH="120px"
                                            maxH="200px"
                                            _placeholder={{ color: 'text.muted' }}
                                            onKeyDown={handleKeyDown}
                                            sx={{ resize: 'none' }}
                                            pr="50px"
                                        />
                                        <Tooltip label="Expand editor (Ctrl+E)" hasArrow>
                                            <IconButton
                                                icon={<MdFullscreen />}
                                                size="sm"
                                                variant="ghost"
                                                position="absolute"
                                                top="8px"
                                                right="8px"
                                                onClick={openModal}
                                                aria-label="Expand editor"
                                                color="text.secondary"
                                                _hover={{ color: 'text.primary', bg: 'rgba(0,0,0,0.05)' }}
                                                zIndex={2}
                                            />
                                        </Tooltip>
                                    </Box>

                                    <HStack spacing={3} justify="space-between" w="full" align="center">
                                        <KeyboardHints />
                                        <HStack spacing={2}>
                                            <QuickTemplatesMenu
                                                onTriggerMouseDown={captureSelectionNow}
                                                getSelection={getCapturedSelection}
                                                getActiveTextarea={getActiveTextarea}
                                                getValue={getMainInputValue}
                                                setValue={setMainInputValue}
                                                size="md"
                                                variant="solid"
                                                colorScheme="gray"
                                                displayMode="icon"
                                            />
                                            <DocumentQuickAccess
                                                onDocumentInsert={handleDocumentInsert}
                                                size="md"
                                                variant="solid"
                                                colorScheme="gray"
                                                displayMode="icon"
                                            />
                                            <Tooltip label="Send message (Enter)" hasArrow>
                                                <IconButton
                                                    type="submit"
                                                    icon={<MdOutlineArrowUpward />}
                                                    size="lg"
                                                    colorScheme="brand"
                                                    isRound
                                                    isLoading={isLoading}
                                                    isDisabled={isLoading}
                                                    aria-label="Send message"
                                                    minW="56px"
                                                    h="56px"
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </HStack>
                                </VStack>

                                {/* ── Input: mobile/tablet ─────────────────── */}
                                <VStack spacing={2} align="stretch" display={{ base: 'flex', lg: 'none' }}>
                                    {/* Textarea row with inline send button */}
                                    <HStack spacing={2} align="flex-end">
                                        <Box flex="1" position="relative">
                                            <Textarea
                                                ref={mobileTextareaRef}
                                                defaultValue=""
                                                variant="messageInput"
                                                placeholder={placeholderText}
                                                onKeyDown={handleKeyDown}
                                                onInput={handleMobileInput}
                                                rows={1}
                                                minH={`${MOBILE_MIN_H}px`}
                                                maxH={`${MOBILE_MAX_H}px`}
                                                py="10px"
                                                pr="40px"
                                                overflow="hidden"
                                                sx={{
                                                    resize: 'none',
                                                    transition: 'height 0.1s ease',
                                                }}
                                                _placeholder={{ color: 'text.muted' }}
                                            />
                                            <IconButton
                                                icon={<MdFullscreen />}
                                                size="xs"
                                                variant="ghost"
                                                position="absolute"
                                                top="8px"
                                                right="6px"
                                                onClick={openModal}
                                                aria-label="Expand editor"
                                                color="text.muted"
                                                _hover={{ color: 'text.primary' }}
                                                zIndex={2}
                                            />
                                        </Box>
                                        <IconButton
                                            type="submit"
                                            icon={<MdOutlineArrowUpward />}
                                            size="md"
                                            colorScheme="brand"
                                            isRound
                                            isLoading={isLoading}
                                            isDisabled={isLoading}
                                            aria-label="Send message"
                                            minW="42px"
                                            h="42px"
                                            flexShrink={0}
                                        />
                                    </HStack>

                                    {/* Compact action row */}
                                    <HStack spacing={1}>
                                        <QuickTemplatesMenu
                                            onTriggerMouseDown={captureSelectionNow}
                                            getSelection={getCapturedSelection}
                                            getActiveTextarea={getActiveTextarea}
                                            getValue={getMainInputValue}
                                            setValue={setMainInputValue}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="gray"
                                            displayMode="icon"
                                        />
                                        <DocumentQuickAccess
                                            onDocumentInsert={handleDocumentInsert}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="gray"
                                            displayMode="icon"
                                        />
                                    </HStack>
                                </VStack>
                            </Box>
                        </VStack>
                    </form>
                </Box>
            </Box>

            {/* ── Expand modal (shared desktop + mobile) ────────────────────── */}
            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                size={modalSize}
                scrollBehavior="inside"
                closeOnOverlayClick={false}
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent
                    maxW={{ base: 'calc(100vw - 16px)', md: '90vw', lg: '80vw', xl: '75vw' }}
                    maxH={{ base: 'calc(100vh - 32px)', md: '90vh' }}
                    minW={{ base: 'calc(100vw - 16px)', md: '600px' }}
                    h={{ base: 'calc(100vh - 32px)', md: 'auto' }}
                    m={{ base: '8px', md: 'auto' }}
                    borderRadius={{ base: 'xl', md: 'lg' }}
                    bg="white"
                    border="1px solid"
                    borderColor="border.primary"
                >
                    <ModalHeader py={3}>
                        <Text fontSize="lg" fontWeight="600">Edit your prompt</Text>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody py={3} flex="1">
                        <Textarea
                            ref={modalTextareaRef}
                            defaultValue=""
                            variant="messageInput"
                            placeholder={modalPlaceholderText}
                            minH={{ base: '50vh', md: '400px' }}
                            maxH={{ base: '60vh', md: '500px' }}
                            sx={{ resize: 'none' }}
                            bg="bg.secondary"
                            resize={{ base: 'none', md: 'vertical' }}
                            autoFocus
                            onKeyDown={handleModalKeyDown}
                        />
                        <Flex justify="flex-end" mt={2}>
                            <KeyboardHints isModal />
                        </Flex>
                    </ModalBody>

                    <ModalFooter py={3}>
                        <HStack
                            spacing={3}
                            w={{ base: 'full', md: 'auto' }}
                            justify={{ base: 'stretch', md: 'flex-end' }}
                        >
                            <QuickTemplatesMenu
                                onTriggerMouseDown={() => {
                                    const ta = modalTextareaRef.current;
                                    if (!ta) return;
                                    const start = ta.selectionStart ?? ta.value.length;
                                    const end = ta.selectionEnd ?? ta.value.length;
                                    selectionSnapshotRef.current = { start, end, text: ta.value.slice(start, end) };
                                }}
                                getSelection={getCapturedSelection}
                                getActiveTextarea={() => modalTextareaRef.current}
                                getValue={() => getRefValue(modalTextareaRef)}
                                setValue={(newValue, caretPos) => setRefValue(modalTextareaRef, newValue, caretPos)}
                                size="md"
                                variant="outline"
                                colorScheme="gray"
                                displayMode="text"
                            />
                            <DocumentQuickAccess
                                onDocumentInsert={handleModalDocumentInsert}
                                size="md"
                                variant="outline"
                                colorScheme="gray"
                                displayMode="text"
                            />
                            <Button
                                variant="ghost"
                                onClick={closeModal}
                                flex={{ base: '1', md: 'none' }}
                                size="md"
                            >
                                Back
                            </Button>
                            <Button
                                colorScheme="brand"
                                rightIcon={<MdOutlineArrowUpward />}
                                onClick={sendFromModal}
                                isDisabled={isLoading}
                                isLoading={isLoading}
                                flex={{ base: '1', md: 'none' }}
                                size="md"
                            >
                                Send
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
});
// NOTE: no custom comparator on purpose (ai-chat#2). handleSubmit closes over
// currentWorkspaceId; a comparator that ignores it leaves this component
// calling a stale handleSubmit after a workspace switch, which files the first
// message in the PREVIOUS workspace. Default shallow compare is correct here.

export default MessageInput;
